import { Base64Strings, usePictureUpload } from '~/common/utils/pictureUtils';
import { PictureType } from '~/common/entities/Pictures/PictureTypes';
import { Flex, FlexProps } from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Text } from '../Text';

const baseStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#333333',
  borderStyle: 'dashed',
  backgroundColor: '#eeeeee',
  color: '#000000',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

type PictureUploadDropAreaProps = FlexProps & {
  pictureType?: PictureType;
  projectOrJobId?: string;
  customHandleFileChange?: (base64StringsArray: Base64Strings) => void;
  customText?: string;
};

export const PictureUploadDropArea: React.FC<PictureUploadDropAreaProps> = ({
  pictureType,
  projectOrJobId,
  width = '100%',
  customHandleFileChange = undefined,
  customText,
  ...props
}) => {
  const { convertFilesToBase64StringAndHandleFileChange } = usePictureUpload();
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ accept: '.jpg' });
  // png doesn't seem to work with the pdf
  // } = useDropzone({ accept: 'image/jpeg, image/png' });

  useEffect(() => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    convertFilesToBase64StringAndHandleFileChange(
      acceptedFiles,
      pictureType,
      projectOrJobId,
      customHandleFileChange,
    );
  }, [acceptedFiles]);

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  return (
    <Flex
      {...getRootProps({ style })}
      width={width}
      {...props}
      justify="center"
      align="center"
      textAlign="center"
      height="2.5vh"
    >
      <input {...getInputProps()} />
      <Text>{customText || 'pictureUploadDropArea.text'}</Text>
    </Flex>
  );
};
